import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useCartStore } from '@/store/cartStore'
import { getShippingCost } from '@/services/productCatalogService'
import { FaShoppingCart, FaTrash, FaPlus, FaMinus, FaArrowRight, FaShieldAlt, FaTruck } from 'react-icons/fa'
import { formatCurrency } from '@/utils/helpers'
import RealisticPlate from '@/components/shop/RealisticPlate'
import { PlateSize, PlateType, VehicleCategory } from '@/services/rules/plateRulesEngine'
import toast from 'react-hot-toast'

function PlateThumb({ customization }: { customization: Record<string, unknown> }) {
  const ps = customization.plateSize as PlateSize || { width: 520, height: 110, layout: 'einzeilig' }
  const RATIO = 560 / 520
  const fullW = ps.width * RATIO
  const fullH = ps.height * RATIO + 28 // plate + size label below
  const scale = 0.55
  return (
    <div style={{ width: fullW * scale, height: fullH * scale }}>
      <div style={{ width: fullW, height: fullH, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <RealisticPlate
          plateSize={ps}
          plateType={customization.plateType as PlateType || 'REGULAR'}
          vehicleCategory={customization.vehicleCategory as VehicleCategory || 'PKW'}
          input={{
            cityCode: String(customization.cityCode || ''),
            letters: String(customization.letters || ''),
            numbers: String(customization.numbers || ''),
          }}
          interactive={false}
        />
      </div>
    </div>
  )
}

function ProductImage({ productId, customization }: { productId: string; customization?: Record<string, unknown> }) {
  if ((productId.startsWith('plate-') || productId.startsWith('PLATE-')) && customization) {
    return (
      <div className="flex items-center">
        <PlateThumb customization={customization} />
      </div>
    )
  }

  if (productId.startsWith('umweltplakette')) {
    return (
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-green-600 border-[3px] border-gray-800 flex items-center justify-center">
        <span className="text-2xl font-black text-gray-900">4</span>
      </div>
    )
  }

  if (productId.startsWith('carbon-upgrade')) {
    return (
      <div className="w-20 h-20 rounded-full border-[3px] border-blue-500 overflow-hidden">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="cartCarbonP" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#2a2a2a" />
              <rect width="3" height="3" fill="#3a3a3a" />
              <rect x="3" y="3" width="3" height="3" fill="#3a3a3a" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#cartCarbonP)" />
        </svg>
      </div>
    )
  }

  const imageMap: Record<string, string> = {
    'halter': '/assets/shop/License plate holder.webp',
    'warndreieck': '/assets/shop/Warning triangle.webp',
    'verbandskasten': '/assets/shop/First aid kit.webp',
    '3in1-set': '/assets/shop/First aid kit.webp',
  }
  const image = imageMap[productId]
  if (image) {
    return <img src={image} alt="" className="w-20 h-20 object-cover rounded-lg" />
  }

  return (
    <div className="w-20 h-20 rounded-lg bg-dark-700 flex items-center justify-center">
      <FaShoppingCart className="w-6 h-6 text-gray-600" />
    </div>
  )
}

function getSubtitle(productId: string, customization?: Record<string, unknown>): string | null {
  if (productId.startsWith('plate-') || productId.startsWith('PLATE-')) {
    const size = customization?.plateSize as { width?: number; height?: number } | undefined
    const parts: string[] = []
    if (size) parts.push(`${size.width} x ${size.height} mm`)
    return parts.length > 0 ? parts.join(' · ') : null
  }
  if (productId.startsWith('umweltplakette')) return 'Schadstoffgruppe 4 (grün)'
  if (productId.startsWith('carbon-upgrade')) return 'DIN-zertifiziert · Carbonoptik'
  return null
}

function isFromWunsch(item: { customization?: Record<string, unknown> }) {
  return item.customization?.source === 'wunschkennzeichen'
}

export default function Cart() {
  const navigate = useNavigate()
  const { items, removeItem, updateQuantity, getTotalPrice, clearCart } = useCartStore()
  const [shippingCost, setShippingCost] = useState(3.45)
  useEffect(() => { getShippingCost().then(setShippingCost) }, [])

  // Remove plate + all linked addons (umweltplakette, carbon)
  const removeWithAddons = (productId: string, customization?: Record<string, unknown>) => {
    removeItem(productId)
    if (customization?.plateRef) {
      const ref = customization.plateRef as string
      items.forEach(i => {
        if (i.productId !== productId && i.customization?.plateRef === ref) {
          removeItem(i.productId)
        }
      })
    }
  }
  const subtotal = getTotalPrice()
  const totalItems = items.reduce((s, i) => s + i.quantity, 0)
  // Only charge shipping if there are physical items (plates, stickers - anything other than digital)
  const hasPhysicalItems = items.some(i =>
    i.productId.startsWith('plate-') || i.productId.startsWith('PLATE-') ||
    i.productId.startsWith('umweltplakette') || i.productId.startsWith('carbon-upgrade') ||
    !i.productId.startsWith('reservation-')
  )
  const shipping = hasPhysicalItems ? shippingCost : 0
  const totalPrice = subtotal + shipping
  const vatAmount = totalPrice * 19 / 119

  if (items.length === 0) {
    return (
      <div className="bg-dark-950 min-h-screen flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="w-24 h-24 rounded-full bg-dark-800 flex items-center justify-center mx-auto mb-6">
            <FaShoppingCart className="w-10 h-10 text-gray-600" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Ihr Warenkorb ist leer</h2>
          <p className="text-gray-500 mb-8">Fügen Sie Produkte hinzu, um fortzufahren</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/wunschkennzeichen')}
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              Wunschkennzeichen
            </button>
            <button
              onClick={() => navigate('/shop')}
              className="px-6 py-2.5 bg-dark-700 hover:bg-dark-600 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              Zum Shop
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="bg-dark-950 min-h-screen py-8 sm:py-12">
      <div className="container-custom px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Warenkorb</h1>
          <p className="text-gray-500 text-sm mt-1">{totalItems} Artikel</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ── Items ──────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-3">
            <AnimatePresence mode="popLayout">
              {items.filter(item => {
                // Hide addon items that are linked to a plate (shown under the plate instead)
                if (!item.productId.startsWith('plate-') && !item.productId.startsWith('PLATE-') && item.customization?.plateRef) {
                  const hasParentPlate = items.some(i => (i.productId.startsWith('plate-') || i.productId.startsWith('PLATE-')) && i.customization?.plateRef === item.customization?.plateRef)
                  if (hasParentPlate) return false
                }
                return true
              }).map((item, idx) => {
                const subtitle = getSubtitle(item.productId, item.customization)
                const locked = isFromWunsch(item)
                const isPlate = item.productId.startsWith('plate-') || item.productId.startsWith('PLATE-')
                // Find addons linked to this plate
                const addons = isPlate && item.customization?.plateRef
                  ? items.filter(i => i.productId !== item.productId && i.customization?.plateRef === item.customization?.plateRef)
                  : []

                return (
                  <motion.div
                    key={item.productId}
                    layout
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -80 }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-dark-900/70 border border-dark-700/40 rounded-xl p-4 sm:p-5"
                  >
                    <div>
                      {/* Plate on its own row */}
                      {isPlate && (
                        <div className="mb-4 flex justify-center">
                          <ProductImage productId={item.productId} customization={item.customization} />
                        </div>
                      )}

                      <div className="flex gap-4">
                        {/* Product image (non-plate) */}
                        {!isPlate && (
                          <div className="flex-shrink-0">
                            <ProductImage productId={item.productId} customization={item.customization} />
                          </div>
                        )}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="text-base font-semibold text-white truncate">{item.productName}</h3>
                              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
                              {locked && <span className="inline-block text-[10px] text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded mt-1">Reservierung</span>}
                            </div>
                          <button
                            onClick={() => { removeWithAddons(item.productId, item.customization); toast.success('Entfernt') }}
                            className="text-gray-600 hover:text-red-400 transition-colors p-1 flex-shrink-0"
                          >
                            <FaTrash className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between mt-4">
                          {/* Quantity */}
                          {locked ? (
                            <span className="text-sm text-gray-400">Anzahl: <span className="text-white font-semibold">{item.quantity}</span></span>
                          ) : (
                            <div className="flex items-center border border-dark-600 rounded-lg overflow-hidden">
                              <button
                                onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                                className="px-3 py-1.5 bg-dark-800 text-gray-400 hover:text-white hover:bg-dark-700 transition-colors"
                              >
                                <FaMinus className="w-2.5 h-2.5" />
                              </button>
                              <span className="px-4 py-1.5 bg-dark-900 text-white font-semibold text-sm min-w-[40px] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                                className="px-3 py-1.5 bg-dark-800 text-gray-400 hover:text-white hover:bg-dark-700 transition-colors"
                              >
                                <FaPlus className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          )}

                          {/* Price */}
                          <div className="text-right">
                            <span className="text-lg font-bold text-white">{formatCurrency(item.price * item.quantity)}</span>
                            {item.quantity > 1 && (
                              <p className="text-xs text-gray-500">{formatCurrency(item.price)} / Stück</p>
                            )}
                          </div>
                        </div>

                        {/* Linked addons shown under plate */}
                        {addons.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-dark-700/30 space-y-2">
                            {addons.map(addon => (
                              <div key={addon.productId} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400">+</span>
                                  <span className="text-gray-300">{addon.productName}</span>
                                </div>
                                <span className="text-gray-200 font-medium">{formatCurrency(addon.price)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </AnimatePresence>

            {/* Clear + continue shopping */}
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => navigate('/shop')}
                className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors"
              >
                Weiter einkaufen
              </button>
              <button
                onClick={() => { clearCart(); toast.success('Warenkorb geleert') }}
                className="text-gray-600 hover:text-red-400 text-sm font-medium transition-colors"
              >
                Warenkorb leeren
              </button>
            </div>
          </div>

          {/* ── Summary ────────────────────────────────────── */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-dark-900/80 border border-dark-700/40 rounded-xl overflow-hidden sticky top-6"
            >
              <div className="px-5 py-4 border-b border-dark-700/40">
                <h2 className="text-base font-bold text-white">Zusammenfassung</h2>
              </div>

              <div className="px-5 py-4 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Zwischensumme ({totalItems} Artikel)</span>
                  <span className="text-gray-200">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Versand</span>
                  <span className={shipping === 0 ? "text-green-400 font-medium" : "text-gray-200"}>
                    {shipping === 0 ? 'Kostenlos' : formatCurrency(shipping)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">inkl. MwSt. (19%)</span>
                  <span className="text-gray-500">{formatCurrency(vatAmount)}</span>
                </div>
                <div className="border-t border-dark-600/50 pt-3 flex justify-between items-end">
                  <span className="text-gray-300 font-semibold">Gesamt</span>
                  <span className="text-2xl font-black text-white">{formatCurrency(totalPrice)}</span>
                </div>
              </div>

              <div className="px-5 pb-5">
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-primary-500/20"
                >
                  Weiter zur Kasse
                  <FaArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Trust */}
              <div className="px-5 py-3 border-t border-dark-700/30 flex items-center justify-center gap-5">
                <div className="flex items-center gap-1.5 text-gray-500 text-[10px]">
                  <FaShieldAlt className="w-3 h-3" />
                  <span>SSL-verschlüsselt</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-500 text-[10px]">
                  <FaTruck className="w-3 h-3" />
                  <span>Schnelle Lieferung</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}
